import { type BreadcrumbSegment, findActiveNavItem } from '@dittolive/anvil'
import { Container, PortalShell } from 'components/layout'
import { useApplyTenant } from 'components/tenant'
import { useAppContext } from 'context'
import { Suspense } from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import { usePersonalNavSections } from './personalNavSections'
import ViewedUserName from './ViewedUserName'

export default function UserManagementLayout() {
  const { userId } = useParams<{ userId: string }>()
  const { pathname } = useLocation()

  const { isSuperAdmin } = useAppContext()

  useApplyTenant(null)

  const navSections = usePersonalNavSections({
    isSuperAdmin,
    userId,
  })

  const activeItem = findActiveNavItem(navSections, pathname)
  const pageName = activeItem?.breadcrumbText ?? activeItem?.text

  const breadcrumbs: BreadcrumbSegment[] = pageName
    ? [{ text: pageName }]
    : deriveBreadcrumbsFromPath(pathname, userId)

  const shell = (tenantBreadcrumbName: string) => (
    <PortalShell
      navSections={navSections}
      breadcrumbs={breadcrumbs}
      showTenantBreadcrumb
      tenantBreadcrumbName={tenantBreadcrumbName}
    >
      <Suspense fallback={null} data-testid="userManagementLayoutSuspense">
        {/* This layout owns the page `Container` for every route below it, so
            child scenes must not render one of their own. Routes that want a
            narrower column are opted in via `isNarrowRoute` below. */}
        <Container narrow={isNarrowRoute(pathname, userId)}>
          <Outlet />
        </Container>
      </Suspense>
    </PortalShell>
  )

  // When an admin views another user's tenant, label the tenant breadcrumb
  // with the viewed user's name instead of "Personal".
  if (userId) {
    return (
      <Suspense fallback={shell('Personal')}>
        <ViewedUserName userId={userId}>
          {(name) => shell(name ?? 'Personal')}
        </ViewedUserName>
      </Suspense>
    )
  }

  return shell('Personal')
}

/** Fallback breadcrumb labels for routes that don't appear in the sidebar nav. */
function deriveBreadcrumbsFromPath(
  pathname: string,
  userId?: string,
): BreadcrumbSegment[] {
  const databasesPath = userId ? `/u/${userId}/databases` : '/databases'

  if (pathname.endsWith('/new-database')) {
    return [{ text: 'Databases', to: databasesPath }, { text: 'New database' }]
  }
  if (pathname.endsWith('/settings')) {
    return [{ text: 'Settings' }]
  }
  if (pathname.endsWith('/notifications')) {
    return [{ text: 'Notifications' }]
  }
  return []
}

/**
 * Routes whose content is a single narrow form and so reads better in a
 * `max-w-xl` column. Every other route uses the default wide container.
 */
function isNarrowRoute(pathname: string, userId?: string): boolean {
  const prefix = userId ? `/u/${userId}` : ''

  return (
    pathname === `${prefix}/new-database` || pathname === `${prefix}/settings`
  )
}
